import type {Auth0Plugin} from '@auth0/auth0-vue';
import type {App} from 'vue';
import {createAuth0} from '@auth0/auth0-vue';
import {storeToRefs} from 'pinia';
import {useEnvironmentStore} from '../env/useEnvironmentStore';

const callbackPath = '/auth/login/auth0/';

/**
 * Create auth0 instance for the application.
 * There are three main flows:
 * 1. Normal login flow
 * 2. Redirect callback flow
 * 3. Impersonation flow
 *
 * The plugin checks the current URL path to determine which flow to execute.
 * After handling the flow, it initializes the Auth0 plugin and attaches it to the Vue app.
 *
 * 1. Normal login flow:
 *    - If the user is not authenticated, it triggers the loginWithRedirect method to redirect the user to Auth0 for authentication.
 *    - If the user is authenticated, it simply attaches the Auth0 instance to the Vue app.
 *
 * 2. Redirect callback flow:
 *    - If the URL path is '/auth/login/auth0/', it means the user has been redirected back from Auth0 after authentication.
 *
 *
 * @returns Vue plugin with an install function
 */
export const createAuthPlugin = (): {
  install: (app: App) => Promise<Auth0Plugin>;
  auth0: Auth0Plugin;
} => {
  // check for redirected url with code and state
  const environmentStore = useEnvironmentStore();
  const {environment} = storeToRefs(environmentStore);
  const impersonatedUserId = getImpersonatedUserId();

  const auth0 = createAuth0({
    audience: environment.value.auth0.audience,
    cacheLocation: 'localstorage',
    client_id: environment.value.auth0.clientId,
    domain: environment.value.auth0.domain,
    scope: determineScope(),
    redirect_uri: `${window.location.origin}${callbackPath}`,
    ...(impersonatedUserId ? {impersonated_user_id: impersonatedUserId} : {}),
  });

  return {
    install: async (app: App) => {
      app.use(auth0);

      const urlParams = new URLSearchParams(window.location.search);
      const isOnCallbackPath = window.location.pathname === callbackPath;
      const isAuth0Callback = isOnCallbackPath && urlParams.has('code') && urlParams.has('state');
      const errorDescription = urlParams.get('error_description');

      if (isOnCallbackPath && errorDescription) {
        await handleError(auth0, errorDescription);
      }

      // On the callback path (success or error) wait indefinitely — the SDK is processing the response.
      // On other pages cap the wait at 2 seconds so a stalled SDK never blocks the app.
      const start = Date.now();
      while (auth0.isLoading.value && Date.now() - start < 2000) await new Promise((r) => setTimeout(r, 100));

      if (isAuth0Callback) {
        handleRedirectCallback();
      } else if (!auth0.isAuthenticated.value) {
        await redirectToLoginPage(auth0);
      }

      return auth0;
    },
    auth0,
  };
};

const determineScope = (): string => {
  const params = new URLSearchParams(window.location.search);
  const path = window.location.pathname;
  const userId = params.get('userId');

  if (path === '/impersonate' && userId) {
    window.localStorage.setItem('auth0_impersonated_account_id', userId);

    return 'impersonation';
  }

  const impersonatedUserId = getImpersonatedUserId();
  return impersonatedUserId ? 'profile email impersonation' : getDefaultScope();
};

const getDefaultScope = () => {
  const environmentStore = useEnvironmentStore();
  const {environment} = storeToRefs(environmentStore);

  return environment.value.auth0.scope;
};

const getImpersonatedUserId = (): string | null => {
  return window.localStorage.getItem('auth0_impersonated_account_id');
};

const redirectToLoginPage = async (auth0: Auth0Plugin) => {
  const currentUrl = window.location.pathname === callbackPath ? window.location.origin + '/' : window.location.href;

  window.localStorage.setItem('returnUrl', currentUrl);
  const url = await auth0.buildAuthorizeUrl({
    appState: {
      target: currentUrl,
    },
  });

  window.location.replace(url);
};

const handleRedirectCallback = () => {
  const returnUrl = localStorage.getItem('returnUrl');
  localStorage.removeItem('returnUrl');

  if (returnUrl) {
    window.location.replace(returnUrl);
  } else {
    window.history.replaceState({}, '', window.location.pathname);
  }
};

const handleError = async (auth0: Auth0Plugin, error: string) => {
  const url = await auth0.buildAuthorizeUrl({
    error: error,
    appState: {
      target: `${window.location.origin}${callbackPath}`,
    },
  });

  await auth0.logout({
    returnTo: url,
  });

  /*
   * Return a never resolving promise to stop the rest of the application and give the code enough time to redirect
   * the browser.
   */
  return new Promise(() => {
    // Empty promise.
  });
};
